import React, {useEffect, useState} from "react";
import { Button, Form, Alert, Spinner, InputGroup, Card, Dropdown } from "react-bootstrap";
import {editAssignment, submitComplaintApi} from "../../http/bookAPI";
import { toast } from "react-toastify";

const AmazonReviewLinkStaffEditor = ({ assignmentData, onSaved, presetStaffComment }) => {
    const [reviewLink, setReviewLink] = useState(assignmentData?.review_link || "");
    const [staffComment, setStaffComment] = useState(assignmentData?.review_link_staff_comment || "");

    // NEW: flow + статус
    const [complaintFlow, setComplaintFlow] = useState(assignmentData?.complaint_flow || "");
    const [selectedStatus, setSelectedStatus] = useState(assignmentData?.check_status || "");

    const [loading, setLoading] = useState(false);
    const [savingStatus, setSavingStatus] = useState(false);
    const [msg, setMsg] = useState({ type: "", text: "" });

    // если прилетел preset — подставляем, но не перетираем, если админ уже правил руками
    const [touched, setTouched] = useState(false);
    useEffect(() => {
        if (!touched && typeof presetStaffComment === "string" && presetStaffComment.trim()) {
            setStaffComment(presetStaffComment.trim());
        }
    }, [presetStaffComment, touched]);

    const onStaffChange = (e) => { setTouched(true); setStaffComment(e.target.value); };

// Три статуса
    const statusOptions = [
        { value: "COMPLETE", label: "COMPLETE" },
        { value: "AWAITING", label: "AWAITING" },
        { value: "COMPLAINT", label: "COMPLAINT" },
        { value: "REFUNDED", label: "REFUNDED" },
        { value: "DECLINED", label: "DECLINED" },
    ];
// внутри AmazonReviewLinkStaffEditor.js, рядом со стейтами selectedStatus / complaintFlow
    const statusTemplates = [
        {
            value: "COMPLETE",
            label: "Set Complete",
            flow: "",
        },
        {
            value: "AWAITING",
            label: "Set Awaiting",
            flow: "",
        },
        {
            value: "COMPLAINT",
            label: "Set Complaint",
            flow: "A complaint was received by staff and is currently under review.",
        },
        {
            value: "REFUNDED",
            label: "Set Refunded",
            flow: "Complaint approved. Assignment reversed and funds refunded to publisher.",
        },
        {
            value: "DECLINED",
            label: "Set Declined",
            flow: "After review, the complaint was declined. The review stands.",
        },
    ];

    const applyStatusTemplate = (tpl) => {
        const ts = new Date().toLocaleString("en-GB", {
            day: "2-digit",
            month: "short",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
            hour12: false,
        });

        // ставим выбранный статус
        setSelectedStatus(tpl.value);

        // дописываем строку в конец complaintFlow
        setComplaintFlow((prev) => {
            const base = (prev ?? "").trim();
            const line = `${tpl.flow} — ${ts}`;
            return base ? `${base}\n${line}` : line;
        });
    };

// 2) форматер штампа времени (Kyiv)
    const makeStamp = () => {
        const s = new Date().toLocaleString("en-GB", {
            timeZone: "Europe/Kyiv",
            day: "2-digit", month: "short", year: "numeric",
            hour: "2-digit", minute: "2-digit", hour12: false
        }).replace(",", "");
        return `(Updated ${s})`;
    };

// 3) аккуратное добавление строки
    const appendTemplateLine = (tpl) => {
        if (!tpl) return;
        setComplaintFlow(prev => {
            const base = (prev ?? "").trimEnd();
            const lastLine = base.split("\n").pop()?.replace(/\s*\(Updated.*\)$/, "")?.trim();
            if (lastLine === tpl.trim()) return base; // ничего не добавляем
            const nl = base.length ? "\n" : "";
            return `${base}${nl}${tpl} ${makeStamp()}`;
        });
    };

// 4) onChange статуса: выбираем статус И добавляем его шаблон
    const onStatusChange = (e) => {
        const val = e.target.value;
        setSelectedStatus(val);
        appendTemplateLine(statusTemplates[val]);
    };

    // ИНИЦИАЛИЗАЦИЯ/СИНХРОНИЗАЦИЯ ИЗ БД
    useEffect(() => {
        setSelectedStatus(assignmentData?.check_status || "");
        setComplaintFlow(assignmentData?.complaint_flow || "");
    }, [
        assignmentData?.id,
        assignmentData?.check_status,
        assignmentData?.complaint_flow
    ]);

    const onFlowChange  = (e) => setComplaintFlow(e.target.value);

    const handleSaveStatus = async () => {
        setMsg({ type: "", text: "" });

        const assignmentId = assignmentData?.id;
        if (!assignmentId) {
            setMsg({ type: "danger", text: "Assignment ID is missing. Please refresh or contact support." });
            return;
        }

        const statusChanged = selectedStatus !== (assignmentData?.check_status || "");
        const flowChanged   = (complaintFlow || "").trim() !== (assignmentData?.complaint_flow || "");

        if (!statusChanged && !flowChanged) {
            setMsg({ type: "info", text: "No changes to save for status/flow." });
            return;
        }
        if (!selectedStatus) {
            setMsg({ type: "warning", text: "Please select a status." });
            return;
        }

        setSavingStatus(true);
        try {
            // сохраняем статус/flow
            const res = await submitComplaintApi({
                assignmentId,
                status: selectedStatus,
                message: (complaintFlow || "").trim(),
            });

            setMsg({ type: "success", text: res?.message || "Status/Flow saved successfully." });
            toast.success("Status/Flow saved.", { position: "bottom-center", autoClose: 3000, hideProgressBar: false });

            // 🔹 дождаться рефетча в родителе
            if (typeof onSaved === "function") {
                await onSaved({
                    check_status: selectedStatus,
                    complaint_flow: (complaintFlow || "").trim(),
                });
            }
        } catch (e) {
            const text = e?.response?.data?.message || e?.message || "Couldn’t save status/flow. Please try again.";
            setMsg({ type: "danger", text });
            toast.error(text, { position: "bottom-center", autoClose: 3000, hideProgressBar: false });
        } finally {
            setSavingStatus(false);
        }
    };



    // --- Save link & staff message ---
    const handleSave = async () => {
        setMsg({ type: "", text: "" });

        const id = assignmentData?.id;
        if (!id) {
            setMsg({ type: "danger", text: "Assignment ID is missing. Please refresh or contact support." });
            return;
        }

        const url = (reviewLink || "").trim();
        const isChanged = url !== (assignmentData?.review_link || "");
        if (isChanged && url && !/^https?:\/\/[^/]*amazon\.[^/]+\/.+/i.test(url)) {
            setMsg({ type: "danger", text: "Please enter a valid Amazon link or leave it blank." });
            return;
        }

        setLoading(true);
        try {
            await editAssignment({
                id,
                review_link: url || null,
                review_link_staff_comment: (staffComment || "").trim() || null,
            });

            setMsg({ type: "success", text: "Success! Changes to link and staff message saved." });
            toast.success("Saved successfully.", { position: "bottom-center", autoClose: 3000, hideProgressBar: false });

            if (typeof onSaved === "function") {
                onSaved({
                    review_link: url || null,
                    review_link_staff_comment: (staffComment || "").trim() || null,
                });
            }
        } catch (e) {
            setMsg({ type: "danger", text: "Couldn’t save. Please try again in a moment." });
            toast.error("Save failed.", { position: "bottom-center", autoClose: 3000, hideProgressBar: false });
        } finally {
            setLoading(false);
        }
    };

    const templates = [
        { label: "ASIN mismatch", text: "Sorry, but this link does not belong to this book, ASINs do not match." },
        { label: "Reviewer name mismatch", text: "Sorry, but the reviewer name on this review does not match yours." },
        { label: "Review too short / low quality", text: "Your review is extremely brief and lacks substance. Please expand it to a few meaningful sentences reflecting your genuine reading experience (what you liked, what could be improved, specifics). Until then, we can’t approve it even though it exists. Thank you for respecting the community standards." },
        { label: "Reviewer name changed", text: "Sorry, but you started this assignment with one reviewer name and then changed it during execution. We cannot confirm." },
        { label: "Review not found on Amazon", text: "We’ve been checking regularly for your review on Amazon, but we still can’t find it. Unless we receive a valid link, we’ll have to remove this assignment and reclaim the Virt that was credited earlier. Please share the review URL if it’s available." },
        { label: "Link not working", text: "Your link does not open. Please check it yourself, nothing is available at this URL." },
        { label: "Not an Amazon review", text: "This link is not related to Amazon reviews. We will continue monitoring, but please replace it with a valid one." },
    ];


    return (
        <Card className="mt-0 p-2 border border-secondary shadow-sm rounded w-100">
            <Form onSubmit={(e) => { e.preventDefault(); handleSave(); }} className="small">
                <InputGroup className="mb-2" size="sm">
                    <Form.Control
                        type="text"
                        placeholder="Amazon review link (optional)…"
                        value={reviewLink}
                        onChange={(e) => setReviewLink(e.target.value)}
                        disabled={loading}
                        size="sm"
                    />
                    <Button variant="success" onClick={handleSave} disabled={loading} type="submit" size="sm">
                        {loading ? <Spinner animation="border" size="sm" /> : "Save"}
                    </Button>
                    <Dropdown align="end">
                        <Dropdown.Toggle variant="secondary" size="sm" id="dropdown-basic">
                            Templates
                        </Dropdown.Toggle>
                        <Dropdown.Menu className="small">
                            {templates.map((tpl, idx) => (
                                <Dropdown.Item key={idx} onClick={() => setStaffComment(tpl.text)} className="small">
                                    {tpl.label}
                                </Dropdown.Item>
                            ))}
                        </Dropdown.Menu>
                    </Dropdown>
                </InputGroup>

                <Form.Control
                    as="textarea"
                    rows={2}
                    placeholder="Staff message to user (optional)…"
                    value={staffComment}
                    onChange={onStaffChange}
                    disabled={loading}
                    size="sm"
                    className="mb-2 small"
                />

                {/* --- STATUS & FLOW BLOCK --- */}
                <div className="border rounded p-2 mb-2">
                    <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-2">
                        <div className=" text-muted">
                            Current:&nbsp;
                            <span className="fw-semibold">{assignmentData?.check_status || "—"}</span>
                            &nbsp;&nbsp; Change to::&nbsp;
                            <span className="fw-semibold">{selectedStatus || "—"}</span>
                        </div>
                        <div className="d-flex align-items-center gap-2">


                            <Dropdown align="end">
                                <Dropdown.Toggle variant="secondary" size="sm" id="dropdown-status-templates">
                                    {selectedStatus || "Status Templates"}
                                </Dropdown.Toggle>
                                <Dropdown.Menu className="small">
                                    {statusTemplates.map((tpl) => (
                                        <Dropdown.Item key={tpl.value} className="small" onClick={() => applyStatusTemplate(tpl)}>
                                            {tpl.label}
                                        </Dropdown.Item>
                                    ))}
                                </Dropdown.Menu>
                            </Dropdown>
                            <Button
                                variant="primary"
                                size="sm"
                                onClick={handleSaveStatus}
                                disabled={
                                    savingStatus ||
                                    loading ||
                                    (
                                        selectedStatus === (assignmentData?.check_status || "") &&
                                        (complaintFlow || "").trim() === (assignmentData?.complaint_flow || "")
                                    ) ||
                                    !selectedStatus
                                }
                            >
                                {savingStatus ? <Spinner animation="border" size="sm" /> : "Save Status"}
                            </Button>
                        </div>

                    </div>

                    {/* редактируемый flow (заполняется при выборе статуса) */}
                    <Form.Control
                        as="textarea"
                        rows={2}
                        placeholder="Complaint flow…"
                        value={complaintFlow}
                        onChange={onFlowChange}
                        disabled={loading}
                        size="sm"
                        className="mb-2 small"
                    />

                </div>


                {msg.type && (
                    <Alert variant={msg.type} className="mb-0 small fw-normal">
                        {msg.text}
                    </Alert>
                )}
            </Form>
        </Card>
    );
};

export default AmazonReviewLinkStaffEditor;
